/* jshint esversion: 6 */
/* jshint esversion: 8 */
/* jshint ignore:start */
import React, { useContext, useEffect, useState } from "react";
import { MyContext } from "../Context/MyProvider";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { showTost } from "../showTost";
import PageHeader from "./PageHeader";
import { FiSettings, FiLock, FiCheckCircle } from "react-icons/fi";

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { basUrl, socketDebugToast, setSocketDebugToast } = useContext(MyContext);
  const [loading, setLoading] = useState(false);

  const [isIncognito, setIsIncognito] = useState(false);
  const [passportActive, setPassportActive] = useState(false);
  const [passportName, setPassportName] = useState("");

  const [incognitoEntitled, setIncognitoEntitled] = useState(null);
  const [passportEntitled, setPassportEntitled] = useState(null);
  const [incognitoMasterEnabled, setIncognitoMasterEnabled] = useState(false);
  const [passportMasterEnabled, setPassportMasterEnabled] = useState(false);

  const getUid = () => {
    try {
      const regUser = JSON.parse(localStorage.getItem("Register_User") || "{}");
      return regUser.id || localStorage.getItem("Uid");
    } catch (_) {
      return localStorage.getItem("Uid");
    }
  };

  const uid = getUid();

  useEffect(() => {
    // Fetch user plan and settings state
    axios.post(`${basUrl}sms_type`)
      .then((res) => {
        setSocketDebugToast(res.data.socket_debug_toast);
        if (res.data.incognito_mode_enabled !== undefined) {
          setIncognitoMasterEnabled(String(res.data.incognito_mode_enabled) === "1");
        }
        if (res.data.passport_mode_enabled !== undefined) {
          setPassportMasterEnabled(String(res.data.passport_mode_enabled) === "1");
        }
      })
      .catch(console.error);

    const isSavedActive = localStorage.getItem("saved_passport_active") === "true";
    const savedName = localStorage.getItem("saved_passport_location_name") || "";
    const savedRadius = localStorage.getItem("rediuse") || "50";
    if (isSavedActive && savedName) {
      setPassportActive(true);
      setPassportName(`${savedName} (${Math.round(parseFloat(savedRadius))} km)`);
    }

    if (uid) {
      axios.post(`${basUrl}home_data`, { uid })
        .then((res) => {
          if (res.data.Result === "true") {
            setIsIncognito(res.data.is_incognito === "1" || res.data.is_incognito === 1);
            if (res.data.incognito_mode_enabled !== undefined) {
              setIncognitoMasterEnabled(String(res.data.incognito_mode_enabled) === "1");
            }
            if (res.data.passport_mode_enabled !== undefined) {
              setPassportMasterEnabled(String(res.data.passport_mode_enabled) === "1");
            }
            if (res.data.is_passport_active === "1" || res.data.is_passport_active === 1 || isSavedActive) {
              setPassportActive(true);
              const pName = res.data.passport_location_name || savedName || "Passport Location";
              setPassportName(`${pName} (${Math.round(parseFloat(savedRadius))} km)`);
            }
          }
        })
        .catch(console.error);

      axios.post(`${basUrl}entitlements`, { uid })
        .then((res) => {
          if (res.data.Result === "true" && res.data.entitlements) {
            setIncognitoEntitled(Boolean(res.data.entitlements.incognito?.allowed));
            setPassportEntitled(Boolean(res.data.entitlements.passport_mode?.allowed));
          }
        })
        .catch(console.error);
    }
  }, [basUrl, setSocketDebugToast, uid]);

  const toggleIncognitoMode = async () => {
    if (!incognitoMasterEnabled) {
      showTost({ title: "Incognito Mode is currently disabled globally by administrator." });
      return;
    }
    const currentUid = getUid();
    if (!currentUid) return showTost({ title: "Login required" });
    setLoading(true);
    try {
      const desired = !isIncognito;
      const res = await axios.post(`${basUrl}toggle_incognito`, { uid: currentUid, is_incognito: desired ? 1 : 0 });
      if (res.data.Result === "true") {
        setIsIncognito(desired);
        showTost({ title: res.data.ResponseMsg || "Incognito setting updated" });
      } else {
        const msg = res.data.ResponseMsg || "Failed to update Incognito mode";
        showTost({ title: msg });
        if (msg.toLowerCase().includes("upgrade")) {
          setTimeout(() => navigate('/upgrade'), 1200);
        }
      }
    } catch (err) {
      showTost({ title: "Error updating Incognito mode" });
    } finally {
      setLoading(false);
    }
  };

  const handlePassportUpdate = async (locationName, lat, long) => {
    if (!passportMasterEnabled) {
      showTost({ title: "Passport Mode is currently disabled globally by administrator." });
      return;
    }
    const currentUid = getUid();
    if (!currentUid) return showTost({ title: "Login required" });
    setLoading(true);
    try {
      const res = await axios.post(`${basUrl}update_passport_location`, {
        uid: currentUid,
        is_passport_active: 1,
        passport_lat: lat,
        passport_long: long,
        passport_location_name: locationName
      });
      if (res.data.Result === "true") {
        setPassportActive(true);
        setPassportName(locationName);
        showTost({ title: res.data.ResponseMsg || "Passport location activated!" });
      } else {
        const msg = res.data.ResponseMsg || "Failed to set Passport location";
        showTost({ title: msg });
        if (msg.toLowerCase().includes("upgrade")) {
          setTimeout(() => navigate('/upgrade'), 1200);
        }
      }
    } catch (err) {
      showTost({ title: "Error updating Passport location" });
    } finally {
      setLoading(false);
    }
  };

  const clearPassport = async () => {
    const currentUid = getUid();
    if (!currentUid) return;
    setLoading(true);
    try {
      await axios.post(`${basUrl}clear_passport_location`, { uid: currentUid }).catch(console.error);
      localStorage.removeItem("saved_passport_active");
      localStorage.removeItem("saved_passport_lat");
      localStorage.removeItem("saved_passport_long");
      localStorage.removeItem("saved_passport_location_name");
      setPassportActive(false);
      setPassportName("");
      showTost({ title: "Reset to real GPS location" });
    } catch (err) {
      showTost({ title: "Error resetting location" });
    } finally {
      setLoading(false);
    }
  };

  const toggleDebugToast = async () => {
    setLoading(true);
    const registerUser = localStorage.getItem("Register_User");
    const user = registerUser ? JSON.parse(registerUser) : null;
    const token = localStorage.getItem("token") || (user && user.token);

    if (!token) {
      showTost({ title: "Authentication required" });
      setLoading(false);
      return;
    }

    const currentVal = socketDebugToast === "1" || socketDebugToast === 1;
    const newVal = currentVal ? "0" : "1";

    try {
      const response = await axios.post(`${basUrl}update_setting`, 
        { socket_debug_toast: newVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.Result === "true") {
        setSocketDebugToast(newVal);
        showTost({ title: t("Settings Updated Successfully") });
      } else {
        showTost({ title: response.data.ResponseMsg || t("Failed to update settings") });
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      showTost({ title: t("Something Went Wrong") });
    } finally {
      setLoading(false);
    }
  };

  const isToastEnabled = socketDebugToast === "1" || socketDebugToast === 1;

  return (
    <div style={{ userSelect: "none", cursor: "default" }}>
      <div className="main-wrapper bg-[#e5e5e5] dashboard">
        <div className="content-body">
          <div className="container-fluid my-2 sm:my-4 px-2.5 sm:px-4">
          <PageHeader
            title={t("Settings")}
            subtitle={t("Manage your account preferences and notification settings")}
            breadcrumbs={[{ label: t("Settings") }]}
            icon={FiSettings}
          />
          <div className="bg-white p-[30px] rounded-2xl shadow-xs max-w-xl mx-auto space-y-6 border border-gray-200/80">

            {/* Incognito Mode Card */}
            {incognitoMasterEnabled && (
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <h3 className="text-[17px] font-bold text-gray-800 flex items-center gap-2 m-0">
                      🕵️ {t("Incognito Mode")}
                    </h3>
                    {incognitoEntitled === false ? (
                      <span
                        onClick={() => navigate('/upgrade')}
                        className="cursor-pointer text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-300/80 hover:bg-amber-100 px-3 py-1 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 shadow-xs transition-colors"
                      >
                        <FiLock className="text-amber-600 text-xs" />
                        <span>{t("Upgrade Plan Required")}</span>
                      </span>
                    ) : incognitoEntitled === true ? (
                      <span className="text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200/80 px-3 py-1 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 shadow-xs">
                        <FiCheckCircle className="text-purple-600 text-xs" />
                        <span>{t("Unlocked by Plan")}</span>
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[14px] text-gray-500 max-w-sm mt-1 m-0">
                    {t("Hide your profile card from general feeds. Only users you have liked can see you.")}
                  </p>
                </div>

                <button
                  disabled={loading}
                  onClick={toggleIncognitoMode}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    isIncognito ? "bg-purple-600" : "bg-gray-300"
                  } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isIncognito ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            )}

            {incognitoMasterEnabled && passportMasterEnabled && <hr className="border-gray-200" />}

            {/* Passport Mode Card */}
            {passportMasterEnabled && (
              <div className="py-2">
                <div className="flex items-center flex-wrap gap-2 mb-1">
                  <h3 className="text-[17px] font-bold text-gray-800 flex items-center gap-2 m-0">
                    ✈️ {t("Passport Mode (Virtual Location)")}
                  </h3>
                  {passportEntitled === false ? (
                    <span
                      onClick={() => navigate('/upgrade')}
                      className="cursor-pointer text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-300/80 hover:bg-amber-100 px-3 py-1 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 shadow-xs transition-colors"
                    >
                      <FiLock className="text-amber-600 text-xs" />
                      <span>{t("Upgrade Plan Required")}</span>
                    </span>
                  ) : passportEntitled === true ? (
                    <span className="text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200/80 px-3 py-1 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 shadow-xs">
                      <FiCheckCircle className="text-purple-600 text-xs" />
                      <span>{t("Unlocked by Plan")}</span>
                    </span>
                  ) : null}
                </div>
                <p className="text-[14px] text-gray-500 mb-3">
                  {t("Swipe and match with candidates anywhere in the world.")}
                </p>

                {passportActive ? (
                  <div className="p-3 bg-purple-50 rounded-xl border border-purple-200 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <span className="text-[12px] font-bold text-purple-700 uppercase tracking-wide">
                        {t("Active Passport Location")}
                      </span>
                      <div className="text-[15px] font-bold text-gray-800 mt-0.5">
                        📍 {passportName}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate('/map')}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-[13px] font-semibold rounded-lg shadow-sm transition-all cursor-pointer"
                      >
                        🗺️ {t("Edit on Map")}
                      </button>
                      <button
                        disabled={loading}
                        onClick={clearPassport}
                        className="px-3 py-1.5 bg-white text-purple-700 text-[13px] font-semibold rounded-lg border border-purple-300 hover:bg-purple-100 transition-all cursor-pointer"
                      >
                        {t("Reset GPS")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 items-center">
                    <button
                      disabled={loading}
                      onClick={() => handlePassportUpdate("Paris, France", 48.8566, 2.3522)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-[13px] rounded-lg hover:bg-purple-50 hover:text-purple-700 border transition-all cursor-pointer"
                    >
                      📍 Paris
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => handlePassportUpdate("New York, USA", 40.7128, -74.0060)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-[13px] rounded-lg hover:bg-purple-50 hover:text-purple-700 border transition-all cursor-pointer"
                    >
                      📍 New York
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => handlePassportUpdate("Tokyo, Japan", 35.6762, 139.6503)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-[13px] rounded-lg hover:bg-purple-50 hover:text-purple-700 border transition-all cursor-pointer"
                    >
                      📍 Tokyo
                    </button>
                    <button
                      onClick={() => navigate('/map')}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-[13px] font-semibold rounded-lg shadow-sm transition-all cursor-pointer"
                    >
                      🗺️ {t("Select Location on Map")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {(incognitoMasterEnabled || passportMasterEnabled) && <hr className="border-gray-200" />}

            {/* Socket Debug Toast Switch */}
            <div className="flex items-center justify-between py-2">
              <div>
                <h3 className="text-[18px] font-semibold text-gray-800">
                  {t("Socket Connection Debug Toasts")}
                </h3>
                <p className="text-[14px] text-gray-500 max-w-sm mt-1">
                  {t("Show interactive connection and disconnection alerts in real-time on both web and mobile app interfaces.")}
                </p>
              </div>

              <button
                disabled={loading}
                onClick={toggleDebugToast}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  isToastEnabled ? "bg-[#980EFF]" : "bg-gray-300"
                } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isToastEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {(incognitoMasterEnabled || passportMasterEnabled) && (
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100 flex items-start gap-3">
                <span className="text-[20px] mt-0.5">ℹ️</span>
                <div className="text-[13px] text-purple-800 leading-relaxed">
                  <strong>{t("How Incognito & Passport work:")}</strong> {t("Incognito hides your card from general feeds until you like a user. Passport Mode updates your virtual location worldwide. Both features adhere to your subscription plan's entitlement rules.")}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
/* jshint ignore:end */
